import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { GameGrid } from "../components/GameGrid";
import { searchGames } from "../data/placeholderGames";

/**
 * Search results page with debounced live filtering. URL ?q= stays in sync for
 * sharing and browser history. Results update as you type (300ms debounce).
 *
 * Popular search suggestions appear when there's no active query.
 */

const POPULAR_SEARCHES = ["runner", "puzzle", "idle", "rpg", "cards", "cozy"];

export function SearchRoute() {
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const urlQuery = params.get("q") ?? "";
	const [inputValue, setInputValue] = useState(urlQuery);
	const [displayQuery, setDisplayQuery] = useState(urlQuery);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const results = searchGames(displayQuery);

	useEffect(() => {
		document.title = displayQuery
			? `"${displayQuery}" — Search · GenieWorlds`
			: "Search · GenieWorlds";
	}, [displayQuery]);

	// Sync input with URL params when they change externally
	useEffect(() => {
		setInputValue(urlQuery);
		setDisplayQuery(urlQuery);
	}, [urlQuery]);

	const handleChange = useCallback(
		(value: string) => {
			setInputValue(value);
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => {
				const trimmed = value.trim();
				setDisplayQuery(trimmed);
				if (trimmed) {
					navigate(
						`/search?q=${encodeURIComponent(trimmed)}`,
						{ replace: true },
					);
				} else {
					navigate("/search", { replace: true });
				}
			}, 300);
		},
		[navigate],
	);

	return (
		<div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8 space-y-8">
			<header className="space-y-4">
				<h1 className="font-display font-extrabold text-3xl md:text-4xl text-ink">
					Find your next game
				</h1>

				{/* Large search input */}
				<div className="relative">
					<input
						type="search"
						value={inputValue}
						onChange={(e) => handleChange(e.target.value)}
						placeholder="Search by title, creator, or tag..."
						autoFocus
						className="w-full h-14 md:h-16 pl-14 pr-6 rounded-2xl bg-surface border border-border text-base md:text-lg text-ink placeholder:text-ink-faint focus:border-primary/60 focus:shadow-[0_0_32px_-8px_rgba(0,255,136,0.2)] focus:outline-none transition-all duration-300"
					/>
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint pointer-events-none"
						aria-hidden="true"
					>
						<circle cx="11" cy="11" r="8" />
						<path d="m21 21-4.3-4.3" />
					</svg>
				</div>

				{displayQuery ? (
					<p className="text-ink-muted">
						{results.length}{" "}
						{results.length === 1 ? "result" : "results"} for{" "}
						<span className="text-ink font-semibold">
							&ldquo;{displayQuery}&rdquo;
						</span>
					</p>
				) : (
					<p className="text-ink-muted">
						Search by title, creator handle, genre, or tag.
					</p>
				)}
			</header>

			{/* Popular searches — shown when no query */}
			{!displayQuery && (
				<section className="space-y-3">
					<h2 className="text-sm font-semibold text-ink-faint uppercase tracking-wider">
						Popular searches
					</h2>
					<div className="flex flex-wrap gap-2">
						{POPULAR_SEARCHES.map((term) => (
							<button
								key={term}
								type="button"
								onClick={() => {
									setInputValue(term);
									setDisplayQuery(term);
									navigate(
										`/search?q=${encodeURIComponent(term)}`,
										{ replace: true },
									);
								}}
								className="px-4 py-2 rounded-full bg-surface border border-border text-sm text-ink-muted hover:text-primary hover:border-primary/30 transition-all duration-200"
							>
								{term}
							</button>
						))}
					</div>
				</section>
			)}

			{/* No results */}
			{displayQuery && results.length === 0 && (
				<div className="py-16 text-center space-y-4">
					<div className="text-6xl" aria-hidden="true">
						🔍
					</div>
					<div className="text-ink-muted text-lg">
						No games matched &ldquo;{displayQuery}&rdquo;
					</div>
					<Link
						to="/browse"
						className="inline-block text-primary hover:underline"
					>
						Browse all games instead
					</Link>
				</div>
			)}

			{results.length > 0 && <GameGrid games={results} />}
		</div>
	);
}
